import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EGRESS_FORM_PARAM, FORM_SERVICE_TYPE } from '../../constants';
import { IEgressFormValidationModel, InputElementModel } from '../../models/index';
import { useSelector } from '../../store/AppStore';
import { isJson } from '../../utilities/services/ServiceEventAssociationUtils';

const EGRESS_VALIDATION_ERRORS = {
  topicName: 'Topic name is invalid',
  topicNameRequired: 'Topic name is required',
  connectionString: 'Connection string is invalid',
  connectionStringRequired: 'Connection string is required',
  partitionId: 'Partition Id is invalid',
  partitionIdRequired: 'Partition Id is required',
  responseAttributeRequired: 'Response attribute is required',
  invalidJSON: 'JSON is invalid'
};

const topicName: InputElementModel = {
  name: 'topicName',
  value: '',
  error: EGRESS_VALIDATION_ERRORS.topicName,
  expression: /^[a-zA-Z]([a-zA-Z0-9-_])*$/,
  isValid: true,
  isTouched: false
};
const partitionId: InputElementModel = {
  name: 'partitionId',
  value: '',
  error: EGRESS_VALIDATION_ERRORS.partitionId,
  expression: /^[\d+$]*$/,
  isValid: true,
  isTouched: false
};
const connectionString: InputElementModel = {
  name: 'connectionString',
  value: '',
  error: '',
  isValid: false,
  isTouched: false
};
const responseAttribute: InputElementModel = {
  name: 'responseAttribute',
  value: '',
  error: '',
  isValid: true,
  isTouched: false
};

const mqResponseAttribute: InputElementModel = {
  name: 'mqResponseAttribute',
  value: '',
  error: '',
  isValid: true,
  isTouched: false
};

export const egressFormParam = {
  topicName,
  partitionId,
  connectionString,
  responseAttribute,
  mqResponseAttribute
};

/**
 * Custom hook to handle Event Form Inputs
 */
export const useEgressFormValidation = () => {
  // Form values
  const [formData, setFormParamValues] = useState(egressFormParam);
  const { serviceType } = useSelector((state) => state.serviceDetailsReducer);
  const { t: translate } = useTranslation();

  const checkValidity = (name: string, value: string) => {
    const keyName = name as keyof IEgressFormValidationModel;

    const regExp = formData[name as keyof IEgressFormValidationModel]
      .expression as RegExp;
    if (formData[name as keyof IEgressFormValidationModel] && !regExp.test(value)) {
      setFormParamValues((previousState: IEgressFormValidationModel) => {
        const data = { ...previousState };
        data[keyName].isValid = false;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        return data;
      });
    } else {
      // set the error state empty or remove the error for service name input
      setFormParamValues((previousState: IEgressFormValidationModel) => {
        const data = { ...previousState };
        data[keyName].isValid = true;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        return data;
      });
    }
  };

  const getFormParam = (previousState: IEgressFormValidationModel, name: string) => {
    const data = { ...previousState };
    const keyName = name as keyof IEgressFormValidationModel;
    const dataName = data[keyName].name;
    const dataValue = data[keyName].value;

    if (!dataValue) {
      if (dataName === EGRESS_FORM_PARAM.topicName) {
        data[keyName].error = String(translate('service_event_form.lbl_topic_name_required'));
      } else {
        data[keyName].error = '';
      }
    } else {
      switch (dataName) {
        case EGRESS_FORM_PARAM.topicName:
          data[keyName].error = String(translate('service_event_form.lbl_topic_name'));
          break;
        case EGRESS_FORM_PARAM.partitionId:
          data[keyName].error = String(translate('service_event_form.lbl_partition_id'));
          break;
        case EGRESS_FORM_PARAM.connectionString:
          data[keyName].error = String(translate('service_event_form.lbl_connection_string'));
          break;
        default:
          data[keyName].error = '';
      }
    }
    return data;
  };

  const getServiceFormValue = (oldvalue: IEgressFormValidationModel, name: string, value: string) => {
    const data = { ...oldvalue };
    const keyName = name as keyof IEgressFormValidationModel;
    data[keyName].value = value;
    return data;
  };

  // A method to handle form inputs
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const maxChars = event.target.maxLength > -1 ? event.target.maxLength : Number.MAX_SAFE_INTEGER;
    const currLength = event.target.value.length;
    const keyName = name as keyof IEgressFormValidationModel;

    if (isJson(value) || value === '' || name === 'connectionString' || serviceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.MQ) {
      setFormParamValues((previousState) => {
        const data = { ...previousState };
        data[keyName].isValid = true;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        data[keyName].error = '';
        return data;
      });
    } else {
      setFormParamValues((previousState) => {
        const data = { ...previousState };
        data[keyName].isValid = false;
        data[keyName].value = value;
        data[keyName].isTouched = true;
        data[keyName].error = String(translate('service_event_form.lbl_invalidJSON'));
        return data;
      });
    }

    if (formData[name as keyof IEgressFormValidationModel].expression) {
      checkValidity(name, value);
    }
    // To check if length exceeds maximum length defined for non-JSON form fields.
    // JSON fields set their own validation message above and should not be reset here.
    if (
      name !== 'responseAttribute'
      && name !== 'mqResponseAttribute'
      && currLength <= maxChars
    ) {
      setFormParamValues((previousState: IEgressFormValidationModel) => getFormParam(previousState, name));
    }

    // validate(event, name, value);
    // Let's set these values in state
    setFormParamValues((oldvalue: IEgressFormValidationModel) => getServiceFormValue(oldvalue, name, value));
  };
  return {
    handleInputChange,
    formData,
    setFormParamValues
  };
};
